import { Diagnostics } from './Diagnostics.mjs';
import { Tokenizer } from './Tokenizer.mjs';

class Parser {
  static #_MAX_DEPTH = 32;
  static #_VALUE_START_TYPES = new Set(['word', 'phrase', 'lparen', 'lbracket', 'lbrace']);
  static #_PRIMARY_START_TYPES = new Set([
    'word', 'phrase', 'lparen', 'lbracket', 'lbrace', 'not-op', 'prefix', 'tilde', 'caret', 'regex'
  ]);
  static #_UNMATCHED_CLOSER_TYPES = new Set(['rparen', 'rbracket', 'rbrace']);

  #_tokens;
  #_position;
  #_diagnostics;
  #_depth;
  #_tooDeepReported;
  #_implicitOperator;
  #_sourceLength;

  constructor(query, implicitOperator) {
    this.#_tokens = Tokenizer.tokenize(query);
    this.#_position = 0;
    this.#_diagnostics = [];
    this.#_depth = 0;
    this.#_tooDeepReported = false;
    this.#_implicitOperator = implicitOperator;
    this.#_sourceLength = query.length;
  }

  static parse(query, options = {}) {
    let implicitOperator = 'and';
    if (options.implicitOperator === 'or') {
      implicitOperator = 'or';
    }
    const parser = new Parser(query, implicitOperator);
    return parser.#_parseQuery();
  }

  #_parseQuery() {
    const parts = [];
    while (this.#_peek().type !== 'eof') {
      const node = this.#_parseOr();
      if (node !== null) {
        parts.push(node);
        continue;
      }
      const stray = this.#_consume();
      if (Parser.#_UNMATCHED_CLOSER_TYPES.has(stray.type)) {
        this.#_diagnostics.push(Diagnostics.error(
          Diagnostics.CODES.UNCLOSED_GROUP, `unmatched '${stray.text}'`, stray
        ));
      } else {
        this.#_diagnostics.push(Diagnostics.error(
          Diagnostics.CODES.UNSUPPORTED_SYNTAX, `unexpected '${stray.text}'`, stray
        ));
      }
    }
    let child = null;
    if (parts.length === 1) {
      child = parts[0];
    } else if (parts.length > 1) {
      child = this.#_combine(this.#_implicitOperator, parts);
    }
    return {
      type: 'query',
      child,
      diagnostics: this.#_diagnostics,
      span: { start: 0, end: this.#_sourceLength }
    };
  }

  #_parseOr() {
    const left = this.#_parseAndLevel();
    if (left === null) {
      return null;
    }
    const children = [left];
    while (true) {
      const next = this.#_peek();
      if (this.#_isOrOperator(next)) {
        const operator = this.#_consume();
        const right = this.#_parseAndLevel();
        if (right === null) {
          this.#_diagnostics.push(Diagnostics.error(
            Diagnostics.CODES.DANGLING_OPERATOR, `'${operator.text}' has no right-hand operand`, operator
          ));
          break;
        }
        children.push(right);
        continue;
      }
      if (this.#_implicitOperator === 'or' && this.#_startsPrimary(next)) {
        const right = this.#_parseAndLevel();
        if (right === null) {
          break;
        }
        children.push(right);
        continue;
      }
      break;
    }
    if (children.length === 1) {
      return left;
    }
    return this.#_combine('or', children);
  }

  #_parseAndLevel() {
    const left = this.#_parseUnary();
    if (left === null) {
      return null;
    }
    const children = [left];
    while (true) {
      const next = this.#_peek();
      if (this.#_isAndOperator(next)) {
        const operator = this.#_consume();
        const right = this.#_parseUnary();
        if (right === null) {
          this.#_diagnostics.push(Diagnostics.error(
            Diagnostics.CODES.DANGLING_OPERATOR, `'${operator.text}' has no right-hand operand`, operator
          ));
          break;
        }
        children.push(right);
        continue;
      }
      if (this.#_implicitOperator === 'and' && this.#_startsPrimary(next)) {
        const right = this.#_parseUnary();
        if (right === null) {
          break;
        }
        children.push(right);
        continue;
      }
      break;
    }
    if (children.length === 1) {
      return left;
    }
    return this.#_combine('and', children);
  }

  #_parseUnary() {
    const next = this.#_peek();
    if (this.#_isNotOperator(next)) {
      const operator = this.#_consume();
      const operand = this.#_parseUnary();
      if (operand === null) {
        this.#_diagnostics.push(Diagnostics.error(
          Diagnostics.CODES.DANGLING_OPERATOR, `'${operator.text}' has no operand`, operator
        ));
        return this.#_missingClause(operator.end);
      }
      return { type: 'not', child: operand, span: { start: operator.start, end: operand.span.end } };
    }
    return this.#_parsePrimary();
  }

  #_parsePrimary() {
    const token = this.#_peek();
    if (token.type === 'prefix' || token.type === 'tilde' || token.type === 'caret' || token.type === 'regex') {
      this.#_consume();
      this.#_diagnostics.push(Diagnostics.error(
        Diagnostics.CODES.UNSUPPORTED_SYNTAX, `'${token.text}' is not FQL syntax`, token
      ));
      return this.#_parsePrimary();
    }
    if (token.type === 'lparen') {
      return this.#_parseGroup();
    }
    if (token.type === 'phrase') {
      const phraseToken = this.#_consume();
      const value = this.#_valueNode(phraseToken);
      this.#_consumeTrailingMarkers();
      return this.#_clause(null, null, value, value.span);
    }
    if (token.type === 'word') {
      if (token.text === 'AND' || token.text === 'OR') {
        return null;
      }
      const wordToken = this.#_consume();
      if (this.#_peek().type === 'colon') {
        return this.#_parseClause(wordToken);
      }
      const value = this.#_valueNode(wordToken);
      this.#_consumeTrailingMarkers();
      return this.#_clause(null, null, value, value.span);
    }
    if (token.type === 'lbracket' || token.type === 'lbrace') {
      const range = this.#_parseRange();
      return this.#_clause(null, null, range, range.span);
    }
    return null;
  }

  #_parseClause(fieldToken) {
    const colon = this.#_consume();
    const next = this.#_peek();
    if (!Parser.#_VALUE_START_TYPES.has(next.type)) {
      this.#_diagnostics.push(Diagnostics.error(
        Diagnostics.CODES.INCOMPLETE_CLAUSE, `'${fieldToken.text}:' has no value`,
        { start: fieldToken.start, end: colon.end }
      ));
      const missing = { type: 'missing', span: { start: colon.end, end: colon.end } };
      return this.#_clause(fieldToken.text, fieldToken, missing, { start: fieldToken.start, end: colon.end });
    }
    if (next.type === 'lparen') {
      return this.#_parseFieldGroup(fieldToken);
    }
    if (next.type === 'lbracket' || next.type === 'lbrace') {
      const range = this.#_parseRange();
      return this.#_clause(fieldToken.text, fieldToken, range, { start: fieldToken.start, end: range.span.end });
    }
    const values = [this.#_valueNode(this.#_consume())];
    this.#_consumeTrailingMarkers();
    while (this.#_peek().type === 'comma') {
      const comma = this.#_consume();
      const valueToken = this.#_peek();
      if (valueToken.type !== 'word' && valueToken.type !== 'phrase') {
        this.#_diagnostics.push(Diagnostics.error(
          Diagnostics.CODES.INCOMPLETE_CLAUSE, `'${fieldToken.text}:' has a trailing comma`, comma
        ));
        break;
      }
      values.push(this.#_valueNode(this.#_consume()));
      this.#_consumeTrailingMarkers();
    }
    if (values.length === 1) {
      return this.#_clause(
        fieldToken.text, fieldToken, values[0], { start: fieldToken.start, end: values[0].span.end }
      );
    }
    return this.#_sugarGroup(fieldToken, values);
  }

  #_parseFieldGroup(fieldToken) {
    const open = this.#_consume();
    if (!this.#_enterGroup()) {
      return this.#_skipBalancedGroup(fieldToken);
    }
    if (this.#_peek().type === 'rparen') {
      const close = this.#_consume();
      this.#_diagnostics.push(Diagnostics.error(
        Diagnostics.CODES.EMPTY_GROUP, `'${fieldToken.text}:()' is empty`,
        { start: fieldToken.start, end: close.end }
      ));
      this.#_leaveGroup();
      const missing = { type: 'missing', span: { start: open.end, end: open.end } };
      return this.#_clause(fieldToken.text, fieldToken, missing, { start: fieldToken.start, end: close.end });
    }
    const values = [];
    let closed = false;
    while (true) {
      const valueToken = this.#_peek();
      if (valueToken.type !== 'word' && valueToken.type !== 'phrase') {
        this.#_diagnostics.push(Diagnostics.error(
          Diagnostics.CODES.UNCLOSED_GROUP, `'${fieldToken.text}:(' is not closed`, open
        ));
        this.#_skipToGroupCloser();
        break;
      }
      values.push(this.#_valueNode(this.#_consume()));
      this.#_consumeTrailingMarkers();
      const next = this.#_peek();
      if (this.#_isOrOperator(next)) {
        this.#_consume();
        continue;
      }
      if (next.type === 'rparen') {
        this.#_consume();
        closed = true;
        break;
      }
      this.#_diagnostics.push(Diagnostics.error(
        Diagnostics.CODES.UNCLOSED_GROUP, `'${fieldToken.text}:(' is not closed`, open
      ));
      this.#_skipToGroupCloser();
      break;
    }
    this.#_leaveGroup();
    if (values.length === 0) {
      const missing = { type: 'missing', span: { start: open.end, end: open.end } };
      return this.#_clause(fieldToken.text, fieldToken, missing, { start: fieldToken.start, end: open.end });
    }
    if (values.length === 1) {
      return this.#_clause(
        fieldToken.text, fieldToken, values[0], { start: fieldToken.start, end: values[0].span.end }
      );
    }
    const group = this.#_sugarGroup(fieldToken, values);
    if (closed) {
      group.span = { start: fieldToken.start, end: this.#_tokens[this.#_position - 1].end };
    }
    return group;
  }

  #_parseGroup() {
    const open = this.#_consume();
    if (!this.#_enterGroup()) {
      return this.#_skipBalancedGroup(null);
    }
    if (this.#_peek().type === 'rparen') {
      const close = this.#_consume();
      this.#_diagnostics.push(Diagnostics.error(
        Diagnostics.CODES.EMPTY_GROUP, "'()' is empty", { start: open.start, end: close.end }
      ));
      this.#_leaveGroup();
      return this.#_missingClause(open.end);
    }
    const inner = this.#_parseOr();
    if (this.#_peek().type === 'rparen') {
      this.#_consume();
    } else {
      this.#_diagnostics.push(Diagnostics.error(
        Diagnostics.CODES.UNCLOSED_GROUP, "'(' is not closed", open
      ));
    }
    this.#_leaveGroup();
    if (inner === null) {
      return this.#_missingClause(open.end);
    }
    return inner;
  }

  #_parseRange() {
    const open = this.#_consume();
    const includeLower = open.type === 'lbracket';
    const lower = this.#_parseRangeBound();
    if (lower === undefined) {
      this.#_diagnostics.push(Diagnostics.error(
        Diagnostics.CODES.UNCLOSED_RANGE, 'range has no lower bound', open
      ));
      return this.#_finishBrokenRange(open, includeLower, null, null);
    }
    const toToken = this.#_peek();
    if (toToken.type !== 'word' || toToken.text !== 'TO') {
      this.#_diagnostics.push(Diagnostics.error(
        Diagnostics.CODES.UNCLOSED_RANGE, "range has no 'TO'", open
      ));
      return this.#_finishBrokenRange(open, includeLower, lower, null);
    }
    this.#_consume();
    const upper = this.#_parseRangeBound();
    if (upper === undefined) {
      this.#_diagnostics.push(Diagnostics.error(
        Diagnostics.CODES.UNCLOSED_RANGE, 'range has no upper bound', open
      ));
      return this.#_finishBrokenRange(open, includeLower, lower, null);
    }
    const closer = this.#_peek();
    let includeUpper = true;
    let end = this.#_tokens[this.#_position - 1].end;
    if (closer.type === 'rbracket' || closer.type === 'rbrace') {
      this.#_consume();
      includeUpper = closer.type === 'rbracket';
      end = closer.end;
    } else {
      this.#_diagnostics.push(Diagnostics.error(
        Diagnostics.CODES.UNCLOSED_RANGE, 'range is not closed', open
      ));
    }
    return {
      type: 'range', lower, upper, includeLower, includeUpper, span: { start: open.start, end }
    };
  }

  #_parseRangeBound() {
    const token = this.#_peek();
    if (token.type !== 'word' && token.type !== 'phrase') {
      return undefined;
    }
    this.#_consume();
    if (token.type === 'word' && token.text === '*') {
      return null;
    }
    return { text: token.text, span: { start: token.start, end: token.end } };
  }

  #_finishBrokenRange(open, includeLower, lower, upper) {
    let end = open.end;
    while (true) {
      const token = this.#_peek();
      if (token.type === 'eof') {
        break;
      }
      if (token.type === 'rbracket' || token.type === 'rbrace') {
        end = this.#_consume().end;
        break;
      }
      end = this.#_consume().end;
    }
    return { type: 'range', lower, upper, includeLower, includeUpper: true, span: { start: open.start, end } };
  }

  #_sugarGroup(fieldToken, values) {
    const children = values.map((value) => this.#_clause(fieldToken.text, fieldToken, value, value.span));
    return {
      type: 'or',
      commaSugar: true,
      children,
      span: { start: fieldToken.start, end: values[values.length - 1].span.end }
    };
  }

  #_valueNode(token) {
    if (token.type === 'phrase') {
      if (token.unterminated === true) {
        this.#_diagnostics.push(Diagnostics.error(
          Diagnostics.CODES.UNCLOSED_PHRASE, 'phrase is not closed', token
        ));
      }
      return { type: 'phrase', text: token.text, span: { start: token.start, end: token.end } };
    }
    return {
      type: 'word', text: token.text, wildcard: token.wildcard === true,
      span: { start: token.start, end: token.end }
    };
  }

  #_clause(field, fieldToken, value, span) {
    let fieldSpan = null;
    if (fieldToken !== null) {
      fieldSpan = { start: fieldToken.start, end: fieldToken.end };
    }
    return { type: 'clause', field, fieldSpan, value, span: { start: span.start, end: span.end } };
  }

  #_missingClause(at) {
    return this.#_clause(null, null, { type: 'missing', span: { start: at, end: at } }, { start: at, end: at });
  }

  #_combine(operator, children) {
    const flattened = [];
    for (const child of children) {
      if (child.type === operator && child.commaSugar !== true) {
        flattened.push(...child.children);
      } else {
        flattened.push(child);
      }
    }
    return {
      type: operator,
      children: flattened,
      span: { start: flattened[0].span.start, end: flattened[flattened.length - 1].span.end }
    };
  }

  #_consumeTrailingMarkers() {
    while (true) {
      const token = this.#_peek();
      if (token.type !== 'tilde' && token.type !== 'caret') {
        return;
      }
      this.#_consume();
      this.#_diagnostics.push(Diagnostics.error(
        Diagnostics.CODES.UNSUPPORTED_SYNTAX, `'${token.text}' is not FQL syntax`, token
      ));
    }
  }

  #_enterGroup() {
    this.#_depth += 1;
    if (this.#_depth <= Parser.#_MAX_DEPTH) {
      return true;
    }
    if (!this.#_tooDeepReported) {
      this.#_tooDeepReported = true;
      const token = this.#_tokens[this.#_position - 1];
      this.#_diagnostics.push(Diagnostics.error(
        Diagnostics.CODES.TOO_DEEP, `grouping exceeds ${Parser.#_MAX_DEPTH} levels`, token
      ));
    }
    return false;
  }

  #_leaveGroup() {
    this.#_depth -= 1;
  }

  #_skipBalancedGroup(fieldToken) {
    let balance = 1;
    let at = this.#_tokens[this.#_position - 1].end;
    while (balance > 0) {
      const token = this.#_peek();
      if (token.type === 'eof') {
        break;
      }
      this.#_consume();
      if (token.type === 'lparen') {
        balance += 1;
      } else if (token.type === 'rparen') {
        balance -= 1;
      }
      at = token.end;
    }
    this.#_leaveGroup();
    if (fieldToken !== null) {
      const missing = { type: 'missing', span: { start: at, end: at } };
      return this.#_clause(fieldToken.text, fieldToken, missing, { start: fieldToken.start, end: at });
    }
    return this.#_missingClause(at);
  }

  #_skipToGroupCloser() {
    while (true) {
      const token = this.#_peek();
      if (token.type === 'eof') {
        return;
      }
      this.#_consume();
      if (token.type === 'rparen') {
        return;
      }
    }
  }

  #_startsPrimary(token) {
    if (!Parser.#_PRIMARY_START_TYPES.has(token.type)) {
      return false;
    }
    if (token.type === 'word' && (token.text === 'AND' || token.text === 'OR')) {
      return false;
    }
    return true;
  }

  #_isOrOperator(token) {
    if (token.type === 'or-op') {
      return true;
    }
    return token.type === 'word' && token.text === 'OR';
  }

  #_isAndOperator(token) {
    if (token.type === 'and-op') {
      return true;
    }
    return token.type === 'word' && token.text === 'AND';
  }

  #_isNotOperator(token) {
    if (token.type === 'not-op') {
      return true;
    }
    return token.type === 'word' && token.text === 'NOT';
  }

  #_peek() {
    return this.#_tokens[this.#_position];
  }

  #_consume() {
    const token = this.#_tokens[this.#_position];
    this.#_position += 1;
    return token;
  }
}

export { Parser };
export default Parser;
